import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { uploadOptimizedImage, reoptimizeStoredImage } from "@/lib/image-upload";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import {
  listSuitesPublic,
  upsertSuite,
  deleteSuite,
  reorderSuites,
  addSuitePhoto,
  deleteSuitePhoto,
  reorderSuitePhotos,
  updateSuitePhotoUrl,
  getSuitesPageContent,
  upsertSuitesContent,
  type SuiteWithPhotos,
  type SuiteChipData,
  type SuitesContent,
} from "@/lib/suites.functions";
import { FieldOverridesButton } from "@/components/admin/overrides/FieldOverridesButton";
import { ImageExperiencesCell } from "@/components/admin/overrides/ImageExperiencesCell";

import {
  ArrowUp,
  ArrowDown,
  Plus,
  Trash2,
  Upload,
  Star,
  ChevronLeft,
  Save,
  X,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/suites")({
  head: () => ({ meta: [{ title: "Suites CMS" }, { name: "robots", content: "noindex" }] }),
  component: SuitesAdmin,
});

function SuitesAdmin() {
  const fetchSuites = useServerFn(listSuitesPublic);
  const reorder = useServerFn(reorderSuites);
  const upsert = useServerFn(upsertSuite);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: suites } = useQuery({
    queryKey: ["admin", "suites"],
    queryFn: () => fetchSuites(),
  });

  const reorderMut = useMutation({
    mutationFn: (ordered_ids: string[]) => reorder({ data: { ordered_ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "suites"] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Reorder failed"),
  });

  const move = (idx: number, dir: -1 | 1) => {
    if (!suites) return;
    const next = [...suites];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    reorderMut.mutate(next.map((s) => s.id));
  };

  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const addMut = useMutation({
    mutationFn: () =>
      upsert({
        data: {
          slug: newSlug.trim(),
          name: newName.trim(),
          tagline: "",
          short_description: "",
          long_description: "",
          occupancy: "",
          bed: "",
          chips: [],
        },
      }),
    onSuccess: () => {
      toast.success("Suite added");
      setNewSlug("");
      setNewName("");
      qc.invalidateQueries({ queryKey: ["admin", "suites"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Add failed"),
  });

  return (
    <div className="fixed inset-0 overflow-y-auto bg-background text-foreground">
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Back to admin
            </Link>
            <h1 className="text-2xl font-serif mt-1">Suites</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage suite content and gallery images. Reorder photos — the first one becomes the suite hero.
            </p>
        </div>

        <SuitesContentPanel />

          <Button variant="outline" size="sm" onClick={() => navigate({ to: "/" })}>View site</Button>
        </div>

        <div className="space-y-4">
          {(suites ?? []).map((s, i) => (
            <SuiteEditor
              key={s.id}
              suite={s}
              index={i}
              total={(suites ?? []).length}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          ))}
        </div>

        <div className="rounded-lg border border-dashed border-border p-4 bg-card/50 space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Add a new suite</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="e.g. forest-suite" />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Forest Suite" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={!newSlug.trim() || !newName.trim() || addMut.isPending} onClick={() => addMut.mutate()}>
              <Plus className="w-4 h-4 mr-2" />{addMut.isPending ? "Adding…" : "Add suite"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuiteEditor({
  suite,
  index,
  total,
  onMoveUp,
  onMoveDown,
}: {
  suite: SuiteWithPhotos;
  index: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const qc = useQueryClient();
  const upsert = useServerFn(upsertSuite);
  const del = useServerFn(deleteSuite);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    slug: suite.slug,
    name: suite.name,
    tagline: suite.tagline,
    short_description: suite.short_description,
    long_description: suite.long_description,
    occupancy: suite.occupancy,
    bed: suite.bed,
    chips: suite.chips,
  });
  useEffect(() => {
    setForm({
      slug: suite.slug,
      name: suite.name,
      tagline: suite.tagline,
      short_description: suite.short_description,
      long_description: suite.long_description,
      occupancy: suite.occupancy,
      bed: suite.bed,
      chips: suite.chips,
    });
  }, [suite]);

  const saveMut = useMutation({
    mutationFn: () => upsert({ data: { id: suite.id, ...form } }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin", "suites"] });
      qc.invalidateQueries({ queryKey: ["suites-public"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: () => del({ data: { id: suite.id } }),
    onSuccess: () => {
      toast.success("Suite deleted");
      qc.invalidateQueries({ queryKey: ["admin", "suites"] });
      qc.invalidateQueries({ queryKey: ["suites-public"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="flex items-center gap-2 min-w-0">
          {suite.photos[0] && (
            <img src={suite.photos[0].url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-serif text-base truncate">{suite.name || "Untitled"}</div>
            <div className="text-xs text-muted-foreground truncate">/{suite.slug} · {suite.photos.length} photo{suite.photos.length === 1 ? "" : "s"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="ghost" disabled={index === 0} onClick={onMoveUp}><ArrowUp className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={index === total - 1} onClick={onMoveDown}><ArrowDown className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>{open ? "Close" : "Edit"}</Button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border p-4 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Tagline</Label>
              <FieldOverridesButton pageKey="suites" fieldKey={`suite.${suite.slug}.tagline`} label={`${suite.name} tagline`} type="text" />
            </div>
            <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Description</Label>
              <FieldOverridesButton pageKey="suites" fieldKey={`suite.${suite.slug}.short_description`} label={`${suite.name} description`} type="longtext" />
            </div>
            <Textarea
              rows={5}
              value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Long description (optional, shown after the main paragraph)</Label>
              <FieldOverridesButton pageKey="suites" fieldKey={`suite.${suite.slug}.long_description`} label={`${suite.name} long description`} type="longtext" />
            </div>
            <Textarea
              rows={3}
              value={form.long_description}
              onChange={(e) => setForm({ ...form, long_description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Occupancy</Label>
              <Input value={form.occupancy} onChange={(e) => setForm({ ...form, occupancy: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Bed</Label>
              <Input value={form.bed} onChange={(e) => setForm({ ...form, bed: e.target.value })} />
            </div>
          </div>

          <ChipsEditor
            chips={form.chips}
            onChange={(chips) => setForm({ ...form, chips })}
          />

          <div className="flex justify-between gap-2 pt-2 border-t border-border">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive"><Trash2 className="w-4 h-4 mr-2" />Delete suite</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{suite.name}"?</AlertDialogTitle>
                  <AlertDialogDescription>This also removes all its photos. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => deleteMut.mutate()}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              <Save className="w-4 h-4 mr-2" />{saveMut.isPending ? "Saving…" : "Save details"}
            </Button>
          </div>

          <PhotosEditor suite={suite} />
        </div>
      )}
    </div>
  );
}

const CHIP_ICONS = ["Eye", "Maximize2", "Sun", "Bath", "Castle", "Star", "Mountain", "Sparkles", "BedDouble", "Users"];

function ChipsEditor({ chips, onChange }: { chips: SuiteChipData[]; onChange: (c: SuiteChipData[]) => void }) {
  const update = (i: number, patch: Partial<SuiteChipData>) => {
    const next = [...chips];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <Label>Feature tags (shown in gold under the gallery)</Label>
      <div className="space-y-2">
        {chips.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <select
              value={c.icon}
              onChange={(e) => update(i, { icon: e.target.value })}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {CHIP_ICONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Input value={c.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Label" />
            <Button size="icon" variant="ghost" onClick={() => onChange(chips.filter((_, j) => j !== i))}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" onClick={() => onChange([...chips, { icon: "Star", label: "" }])}>
        <Plus className="w-4 h-4 mr-2" />Add tag
      </Button>
    </div>
  );
}

function PhotosEditor({ suite }: { suite: SuiteWithPhotos }) {
  const qc = useQueryClient();
  const addPhoto = useServerFn(addSuitePhoto);
  const delPhoto = useServerFn(deleteSuitePhoto);
  const reorderPhotos = useServerFn(reorderSuitePhotos);
  const updatePhotoUrl = useServerFn(updateSuitePhotoUrl);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState<{ done: number; total: number } | null>(
    null,
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "suites"] });
    qc.invalidateQueries({ queryKey: ["suites-public"] });
  };

  const reorderMut = useMutation({
    mutationFn: (ordered_ids: string[]) => reorderPhotos({ data: { suite_id: suite.id, ordered_ids } }),
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Reorder failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delPhoto({ data: { id } }),
    onSuccess: () => { toast.success("Photo removed"); invalidate(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...suite.photos];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    reorderMut.mutate(next.map((p) => p.id));
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const { url, path } = await uploadOptimizedImage({
          file,
          bucket: "slide-media",
          folder: `suites/${suite.id}`,
        });
        await addPhoto({ data: { suite_id: suite.id, url, storage_path: path } });
      }
      toast.success("Photos uploaded");
      invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const needsOpt = suite.photos.filter((p) => {
    const u = p.url.split("?")[0].toLowerCase();
    return !u.endsWith(".webp") && !u.endsWith(".svg") && !u.endsWith(".gif");
  });

  const optimizeExisting = async () => {
    if (needsOpt.length === 0) {
      toast.success("All photos are already optimized");
      return;
    }
    setOptimizing({ done: 0, total: needsOpt.length });
    let failed = 0;
    for (let i = 0; i < needsOpt.length; i++) {
      const p = needsOpt[i];
      try {
        const res = await reoptimizeStoredImage({
          url: p.url,
          bucket: "slide-media",
          folder: `suites/${suite.id}`,
        });
        if (res) {
          await updatePhotoUrl({
            data: { id: p.id, url: res.url, storage_path: res.path },
          });
        }
      } catch (e) {
        console.warn("Suite photo re-optimize failed", e);
        failed++;
      }
      setOptimizing({ done: i + 1, total: needsOpt.length });
    }
    setOptimizing(null);
    if (failed) {
      toast.warning(
        `Optimized ${needsOpt.length - failed} of ${needsOpt.length} photos (${failed} failed)`,
      );
    } else {
      toast.success(`Optimized ${needsOpt.length} photo${needsOpt.length === 1 ? "" : "s"}`);
    }
    invalidate();
  };

  return (
    <div className="space-y-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label>Gallery photos</Label>
        <div className="flex gap-2">
          {needsOpt.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              disabled={optimizing !== null}
              onClick={optimizeExisting}
            >
              {optimizing
                ? `Optimizing ${optimizing.done}/${optimizing.total}`
                : `Optimize ${needsOpt.length} existing`}
            </Button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" />{uploading ? "Optimizing…" : "Upload photos"}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Images are auto-optimized to WebP, max 2048px wide. HEIC photos from iPhone are converted automatically. The first photo is the suite hero.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {suite.photos.map((p, i) => (
          <div key={p.id} className="rounded-lg overflow-hidden border border-border bg-muted">
            <div className="relative aspect-[4/3]">
              <img src={p.url} alt={p.alt} className="absolute inset-0 w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-gold/90 text-black text-[10px] uppercase tracking-wider px-2 py-0.5">
                  <Star className="w-3 h-3" />Hero
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 flex items-center justify-between">
                <div className="flex gap-1">
                  <Button size="icon" variant="secondary" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="secondary" className="h-7 w-7" disabled={i === suite.photos.length - 1} onClick={() => move(i, 1)}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => delMut.mutate(p.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <ImageExperiencesCell imageTable="suite_photos" imageId={p.id} />
          </div>
        ))}
      </div>
      {suite.photos.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No photos yet. Upload to add gallery images.
        </div>
      )}
    </div>
  );
}

const SUITES_PAGE_KEY = "suites";

const uploadSuitesImage = (folder: string) => async (f: File) => {
  const { url } = await uploadOptimizedImage({
    file: f,
    bucket: "slide-media",
    folder: `suites/page/${folder}`,
  });
  return url;
};

function SuitesContentPanel() {
  const fetchContent = useServerFn(getSuitesPageContent);
  const upsert = useServerFn(upsertSuitesContent);
  const qc = useQueryClient();

  const { data: content } = useQuery({
    queryKey: ["admin", "suites-content"],
    queryFn: () => fetchContent(),
  });

  const [form, setForm] = useState<{
    hero_image_url: string;
    hero_kicker: string;
    hero_title: string;
    hero_subtitle: string;
    amenities_kicker: string;
    amenities_image_url: string;
    amenities_text: string;
  } | null>(null);

  useEffect(() => {
    if (!content) return;
    setForm({
      hero_image_url: content.hero_image_url ?? "",
      hero_kicker: content.hero_kicker ?? "",
      hero_title: content.hero_title ?? "",
      hero_subtitle: content.hero_subtitle ?? "",
      amenities_kicker: content.amenities_kicker ?? "",
      amenities_image_url: content.amenities_image_url ?? "",
      amenities_text: (content.amenities ?? []).join("\n"),
    });
  }, [content]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "suites-content"] });
    qc.invalidateQueries({ queryKey: ["suites-content"] });
  };

  const persist = async (next: typeof form) => {
    if (!next) return;
    await upsert({
      data: {
        id: content?.id,
        hero_image_url: next.hero_image_url || null,
        hero_kicker: next.hero_kicker || null,
        hero_title: next.hero_title || null,
        hero_subtitle: next.hero_subtitle || null,
        amenities_kicker: next.amenities_kicker || null,
        amenities_image_url: next.amenities_image_url || null,
        amenities: next.amenities_text
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      },
    });
    invalidate();
  };

  const save = useMutation({
    mutationFn: () => persist(form),
    onSuccess: () => toast.success("Saved"),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const heroFileRef = useRef<HTMLInputElement | null>(null);
  const amenFileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState<null | "hero" | "amen">(null);

  const handleUpload = async (kind: "hero" | "amen", f: File) => {
    if (!form) return;
    setUploading(kind);
    try {
      const folder = kind === "hero" ? "hero" : "amenities";
      const url = await uploadSuitesImage(folder)(f);
      const next = {
        ...form,
        ...(kind === "hero"
          ? { hero_image_url: url }
          : { amenities_image_url: url }),
      };
      setForm(next);
      await persist(next);
      toast.success("Image saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(null);
      if (heroFileRef.current) heroFileRef.current.value = "";
      if (amenFileRef.current) amenFileRef.current.value = "";
    }
  };

  if (!form) {
    return (
      <div className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
        Loading page content…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-6">
      <div>
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Page hero
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          The image and text shown at the top of the Suites page.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Hero image</Label>
          <FieldOverridesButton
            pageKey={SUITES_PAGE_KEY}
            fieldKey="page.hero_image"
            label="Hero image"
            type="image"
            onUploadImage={uploadSuitesImage("hero")}
          />
        </div>
        <div className="flex items-start gap-3">
          {form.hero_image_url ? (
            <img src={form.hero_image_url} alt="" className="w-32 h-32 object-cover rounded" />
          ) : (
            <div className="w-32 h-32 rounded bg-muted" />
          )}
          <div className="flex-1 space-y-2">
            <Input
              value={form.hero_image_url}
              onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
              placeholder="https://… or upload"
            />
            <input
              ref={heroFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload("hero", f);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => heroFileRef.current?.click()}
              disabled={uploading === "hero"}
            >
              <Upload className="w-4 h-4 mr-1" />
              {uploading === "hero" ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Kicker</Label>
          <FieldOverridesButton pageKey={SUITES_PAGE_KEY} fieldKey="page.hero_kicker" label="Hero kicker" type="text" />
        </div>
        <Input value={form.hero_kicker} onChange={(e) => setForm({ ...form, hero_kicker: e.target.value })} placeholder="ONE OF A KIND" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Title</Label>
          <FieldOverridesButton pageKey={SUITES_PAGE_KEY} fieldKey="page.hero_title" label="Hero title" type="text" />
        </div>
        <Textarea
          rows={2}
          value={form.hero_title}
          onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
          placeholder={"Nature Suites\n(press Enter to break line)"}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Subtitle</Label>
          <FieldOverridesButton pageKey={SUITES_PAGE_KEY} fieldKey="page.hero_subtitle" label="Hero subtitle" type="longtext" />
        </div>
        <Textarea
          value={form.hero_subtitle}
          rows={3}
          onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })}
        />
      </div>

      <div className="pt-2 border-t border-border">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Every nature suite includes
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          The amenities section shown below the suite selector.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Section image</Label>
          <FieldOverridesButton
            pageKey={SUITES_PAGE_KEY}
            fieldKey="page.amenities_image"
            label="Amenities image"
            type="image"
            onUploadImage={uploadSuitesImage("amenities")}
          />
        </div>
        <div className="flex items-start gap-3">
          {form.amenities_image_url ? (
            <img src={form.amenities_image_url} alt="" className="w-32 h-32 object-cover rounded" />
          ) : (
            <div className="w-32 h-32 rounded bg-muted" />
          )}
          <div className="flex-1 space-y-2">
            <Input
              value={form.amenities_image_url}
              onChange={(e) => setForm({ ...form, amenities_image_url: e.target.value })}
              placeholder="https://… or upload"
            />
            <input
              ref={amenFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload("amen", f);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => amenFileRef.current?.click()}
              disabled={uploading === "amen"}
            >
              <Upload className="w-4 h-4 mr-1" />
              {uploading === "amen" ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Section kicker</Label>
          <FieldOverridesButton pageKey={SUITES_PAGE_KEY} fieldKey="page.amenities_kicker" label="Amenities kicker" type="text" />
        </div>
        <Input
          value={form.amenities_kicker}
          onChange={(e) => setForm({ ...form, amenities_kicker: e.target.value })}
          placeholder="Every nature suite includes"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Amenities — one per line</Label>
          <FieldOverridesButton
            pageKey={SUITES_PAGE_KEY}
            fieldKey="page.amenities"
            label="Amenities list"
            type="longtext"
          />
        </div>
        <Textarea
          value={form.amenities_text}
          onChange={(e) => setForm({ ...form, amenities_text: e.target.value })}
          rows={8}
          placeholder={"Panoramic windows\nKing-sized beds\n…"}
        />
        <p className="text-xs text-muted-foreground mt-1">
          One amenity per line. Blank lines are ignored.
        </p>
      </div>

      <div className="flex justify-end pt-2 border-t border-border">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          <Save className="w-4 h-4 mr-2" />
          {save.isPending ? "Saving…" : "Save page content"}
        </Button>
      </div>
    </div>
  );
}
